import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { withTranslation, Trans } from 'react-i18next'
import useLocalStorage from 'react-use-localstorage'

import './EditorHintLayer.scss'

import slideTipImg from '../../images/png/editorTips/slides.png'
import expertModeTipImg from '../../images/png/editorTips/expertMode.png'
import deviceChangerTipImg from '../../images/png/editorTips/deviceChanger.png'
import fbChatTipImg from '../../images/png/editorTips/fbChat.png'
import memoryClick from '../../images/png/editorTips/memory-click.png'
import memoryDoubleClick from '../../images/png/editorTips/memory-dclick.png'

const EDIT_HINT_LAYER__SHOWED_IDS = 'EDIT_HINT_LAYER__SHOWED_IDS'

const tipsConfig = [
    {
        id: 'editor-tip__slide_group',
        highlightSize: 162,
        getTargetForTip: () => {
            const containerId = 'editor-tip__slide_group'
            const targetId = 'editor-tip__screen_slide'
            const containerGroupAttr = 'data-app-property'
            const needContainerGroupVal = '_group'
            const startGroup = 'start_group'

            const elements = [
                ...document.querySelectorAll(
                    `#${containerId}[${containerGroupAttr}$=${needContainerGroupVal}]:not([${containerGroupAttr}$=${startGroup}])  #${targetId}`,
                ),
            ]

            return elements[0]
        },
        tipImg: slideTipImg,
        tip: <Trans i18nKey="_pages._editor._hints._tips._1" />,
        notSupported: ['RANK_BATTLE', 'BEFORE_AFTER', 'MEMORY'],
    },
    {
        id: 'editor-tip__expert_mode',
        highlightSize: 212,
        getTargetForTip: () => {
            const targetId = 'editor-tip__expert_mode'

            return document.querySelector(`#${targetId}`)
        },
        tipImg: expertModeTipImg,
        tip: <Trans i18nKey="_pages._editor._hints._tips._2" />,
    },
    {
        id: 'editor-tip__device-changer',
        highlightSize: 125,
        getTargetForTip: () => {
            const targetId = 'editor-tip__device-changer'

            return document.querySelector(`#${targetId}`)
        },
        tipImg: deviceChangerTipImg,
        tip: <Trans i18nKey="_pages._editor._hints._tips._3" />,
    },
    {
        id: 'editor-tip__fb-mock',
        highlightSize: 197,
        getTargetForTip: () => {
            const targetId = 'editor-tip__fb-mock'

            return document.querySelector(`#${targetId}`)
        },
        tipImg: fbChatTipImg,
        tip: <Trans i18nKey="_pages._editor._hints._tips._4" />,
    },
    {
        id: 'editor-tip__memory_click',
        highlightSize: 0,
        noHighlight: true,
        tipImg: memoryClick,
        tip: 'Click on the cards to open the settings panel',
        supported: ['MEMORY'],
    },
    {
        id: 'editor-tip__memory_double-click',
        highlightSize: 0,
        noHighlight: true,
        tipImg: memoryDoubleClick,
        tip: 'Double click to edit text',
        supported: ['MEMORY'],
    },
]

const getHighlightRect = (rect, highlightSize) => {
    const highlightTop = rect.y + (rect.height - highlightSize) / 2
    const highlightLeft = rect.x + (rect.width - highlightSize) / 2

    return { y: highlightTop, x: highlightLeft, width: highlightSize, height: highlightSize }
}

const tipsConfigByProjectType = (configs = tipsConfig, projectType) => {
    return configs.filter(tipConfig => {
        if (tipConfig.supported && tipConfig.supported.length !== 0) {
            return tipConfig.supported.includes(projectType)
        } else {
            if (!tipConfig.notSupported || tipConfig.notSupported.length === 0) {
                return true
            } else {
                return !tipConfig.notSupported.includes(projectType)
            }
        }
    })
}

const tipsConfigByShowedIds = (configs = tipsConfig, showedIds) => {
    return configs.filter(tipConfig => !showedIds.includes(tipConfig.id))
}

const EditorHintLayer = ({ t }) => {
    const [isOpen, setOpen] = useState(true)

    const [showedIds, setShowedIds] = useLocalStorage(EDIT_HINT_LAYER__SHOWED_IDS, JSON.stringify([]))

    const [highlightRect, setHighlightRect] = useState(void 0)
    const [tipIndex, setTipIndex] = useState(0)

    const projectType = useSelector(state => state.editor.projectType.projectType)

    useEffect(() => {
        const config = tipsConfigByShowedIds(tipsConfigByProjectType(tipsConfig, projectType), JSON.parse(showedIds))

        if (!projectType || !isOpen || tipIndex + 1 > config.length) return
        const tip = config[tipIndex]

        if (tip.noHighlight) {
            setHighlightRect(void 0)
            return
        }

        const some = tip.getTargetForTip()
        if (!some) return

        const rect = some.getBoundingClientRect()

        setHighlightRect(getHighlightRect(rect, tip.highlightSize))
    }, [tipIndex])

    const config = tipsConfigByShowedIds(tipsConfigByProjectType(tipsConfig, projectType), JSON.parse(showedIds))

    if (!projectType || !isOpen || tipIndex + 1 > config.length) return null

    return (
        <>
            <div className="editor-hint-layer__highlight-container">
                {highlightRect === void 0 ? null : (
                    <div
                        className="editor-hint-layer__highlight-wrapper"
                        style={{
                            position: 'absolute',
                            left: `${highlightRect.x}px`,
                            top: `${highlightRect.y - 60}px`,
                            width: `${highlightRect.width}px`,
                            height: `${highlightRect.height}px`,
                            borderRadius: `${highlightRect.width / 2}px`,
                        }}
                    ></div>
                )}
            </div>
            <div className="editor-hint-layer__modal_container">
                <div className="editor-hint-layer__modal">
                    <div className="editor-hint-layer__modal__head">
                        <div
                            className="editor-hint-layer__modal__skip"
                            onClick={() => {
                                setOpen(false)
                                setShowedIds(
                                    JSON.stringify([...config.map(config => config.id), ...JSON.parse(showedIds)]),
                                )
                            }}
                        >
                            {t('_pages._editor._hints.skip')}
                        </div>
                        <p>
                            <Trans i18nKey="_pages._editor._hints.title" />
                        </p>
                    </div>
                    <div className="editor-hint-layer__modal__hint-img">
                        <img src={config[tipIndex].tipImg}></img>
                    </div>
                    <div className="editor-hint-layer__modal__progress-num">
                        {tipIndex + 1}/{config.length}
                    </div>
                    <div className="editor-hint-layer__modal__tip">{config[tipIndex].tip}</div>
                    <div className="editor-hint-layer__modal__navigation">
                        <button
                            className="button"
                            data-variant="secondary"
                            data-size="md"
                            disabled={tipIndex === 0}
                            onClick={() => setTipIndex(tipIndex - 1)}
                        >
                            {t('_pages._editor._hints.previous')}
                        </button>
                        <div className="dots">
                            {config.map((_, i) => {
                                return (
                                    <div
                                        key={i}
                                        className={`dot${tipIndex === i ? ' highlight' : ''}`}
                                        onClick={tipIndex !== i ? () => setTipIndex(i) : void 0}
                                    ></div>
                                )
                            })}
                        </div>
                        {tipIndex + 1 >= config.length ? (
                            <button
                                className="button"
                                data-variant="primary"
                                data-size="md"
                                onClick={() => {
                                    setTipIndex(tipIndex + 1)
                                    setShowedIds(
                                        JSON.stringify([
                                            ...config.slice(0, tipIndex + 1).map(config => config.id),
                                            ...JSON.parse(showedIds),
                                        ]),
                                    )
                                }}
                            >
                                {t('_pages._editor._hints.ok')}
                            </button>
                        ) : (
                            <button
                                className="button"
                                data-variant="primary"
                                data-size="md"
                                onClick={() => setTipIndex(tipIndex + 1)}
                            >
                                {t('_pages._editor._hints.next')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}

export default withTranslation('translations')(EditorHintLayer)
